'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { staggerContainer, staggerItem, tooltipVariants } from '@/lib/animations';

export type NavigationSection = 'stage1' | 'location' | 'documents' | 'ENVIRONMENT' | 'DESIGN' | 'ECONOMIC' | 'SOCIAL' | 'PREVIEW' | 'SCORE';

interface StageNavigationProps {
    isReadOnly?: boolean;
    activeSection: NavigationSection;
    onNavigate: (section: NavigationSection) => void;
    onSave?: () => void;
    onComplete?: () => void;
    onDownloadPdf?: () => void;
    saving?: boolean;
    downloadingPdf?: boolean;
}

interface NavItem {
    id: NavigationSection;
    icon: React.ReactNode;
    label: string;
    type: 'scroll' | 'tab';
    color?: string;
}

const navItems: NavItem[] = [
    // Scroll sections
    {
        id: 'stage1',
        label: 'Building Characterization',
        type: 'scroll',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
        ),
    },
    {
        id: 'location',
        label: 'Location',
        type: 'scroll',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        ),
    },
    {
        id: 'documents',
        label: 'Documents',
        type: 'scroll',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        ),
    },
    // Cluster tabs
    {
        id: 'ENVIRONMENT',
        label: 'Environment',
        type: 'tab',
        color: 'emerald',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
        ),
    },
    {
        id: 'DESIGN',
        label: 'Design',
        type: 'tab',
        color: 'blue',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
        ),
    },
    {
        id: 'ECONOMIC',
        label: 'Economic',
        type: 'tab',
        color: 'amber',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
    {
        id: 'SOCIAL',
        label: 'Social',
        type: 'tab',
        color: 'rose',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
        ),
    },
    // Preview
    {
        id: 'PREVIEW',
        label: 'Preview',
        type: 'scroll',
        color: 'violet',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
        ),
    },
    // Score
    {
        id: 'SCORE',
        label: 'Score',
        type: 'scroll',
        color: 'indigo',
        icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    },
];

export function StageNavigation({ isReadOnly = false, activeSection, onNavigate, onSave, onComplete, onDownloadPdf, saving, downloadingPdf }: StageNavigationProps) {
    const t = useTranslations('stageNavigation');
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);

    const scrollSections = navItems.filter(item => item.type === 'scroll' && item.id !== 'PREVIEW' && item.id !== 'SCORE');
    const clusterSections = navItems.filter(item => item.type === 'tab');
    const previewSection = navItems.find(item => item.id === 'PREVIEW')!;
    const scoreSection = navItems.find(item => item.id === 'SCORE')!;

    const getActiveStyles = (item: NavItem, isActive: boolean) => {
        if (!isActive) {
            return 'bg-white hover:bg-slate-50 text-slate-600 border-l-2 border-l-transparent border-y border-r border-slate-200';
        }
        switch (item.color) {
            case 'emerald':
                return 'bg-emerald-50 border-l-4 border-l-emerald-500 border-y border-r border-emerald-200 text-emerald-700';
            case 'blue':
                return 'bg-blue-50 border-l-4 border-l-blue-500 border-y border-r border-blue-200 text-blue-700';
            case 'amber':
                return 'bg-amber-50 border-l-4 border-l-amber-500 border-y border-r border-amber-200 text-amber-700';
            case 'rose':
                return 'bg-rose-50 border-l-4 border-l-rose-500 border-y border-r border-rose-200 text-rose-700';
            case 'violet':
                return 'bg-violet-50 border-l-4 border-l-violet-500 border-y border-r border-violet-200 text-violet-700';
            default:
                return 'bg-indigo-50 border-l-4 border-l-indigo-500 border-y border-r border-indigo-200 text-indigo-700';
        }
    };

    const renderNavItem = (item: NavItem) => {
        const isActive = activeSection === item.id;
        const isHovered = hoveredItem === item.id;

        return (
            <motion.div key={item.id} className="relative" variants={staggerItem}>
                <motion.button
                    onClick={() => onNavigate(item.id)}
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className={`w-9 h-9 flex items-center justify-center transition-all duration-200 cursor-pointer ${getActiveStyles(item, isActive)}`}
                    aria-label={t(`sections.${item.id}`)}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                    {item.icon}
                </motion.button>

                {/* Tooltip */}
                <AnimatePresence>
                    {isHovered && (
                        <motion.div
                            className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
                            variants={tooltipVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="bg-slate-800/80 backdrop-blur-md text-white text-sm px-3 py-1.5 whitespace-nowrap shadow-lg border border-white/10">
                                {t(`sections.${item.id}`)}
                                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        );
    };



    return (
        <motion.nav
            id="stage-navigation"
            className="fixed left-4 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-2 p-2"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
        >

            {/* Scroll sections */}
            <motion.div className="flex flex-col gap-2" variants={staggerContainer}>
                {scrollSections.map(renderNavItem)}
            </motion.div>


            {/* Separator */}
            <motion.div className="w-8 mx-auto h-px bg-slate-200 my-1" variants={staggerItem} />

            {/* Cluster tabs */}
            <motion.div className="flex flex-col gap-2" variants={staggerContainer}>
                {clusterSections.map(renderNavItem)}
            </motion.div>

            {/* Separator */}
            <motion.div className="w-8 mx-auto h-px bg-slate-200 my-1" variants={staggerItem} />

            {/* Preview & Score */}
            {renderNavItem(previewSection)}
            {renderNavItem(scoreSection)}

            {/* Action buttons - Save & Complete */}
            {(!isReadOnly && (onSave || onComplete)) && (
                <>
                    <motion.div className="w-8 mx-auto h-px bg-slate-200 my-6" variants={staggerItem} />

                    <motion.div className="flex flex-col gap-2" variants={staggerContainer}>
                        {/* Save button */}
                        {onSave && (
                            <motion.div className="relative" variants={staggerItem}>
                                <motion.button
                                    onClick={onSave}
                                    disabled={saving}
                                    onMouseEnter={() => setHoveredItem('save')}
                                    onMouseLeave={() => setHoveredItem(null)}
                                    className={`w-9 h-9 flex items-center justify-center transition-all duration-200 cursor-pointer ${saving
                                        ? 'bg-emerald-100 text-emerald-400 border-l-2 border-l-emerald-300 border-y border-r border-emerald-200'
                                        : 'bg-emerald-50 hover:bg-emerald-500 border-l-4 border-l-emerald-500 hover:border-l-emerald-600 border-y border-r border-emerald-200 text-emerald-700 hover:text-white'
                                        }`}
                                    aria-label="Save"
                                    whileTap={{ scale: 0.95 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                >
                                    {saving ? (
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                        </svg>
                                    )}
                                </motion.button>

                                <AnimatePresence>
                                    {hoveredItem === 'save' && (
                                        <motion.div
                                            className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
                                            variants={tooltipVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <div className="bg-slate-800/80 backdrop-blur-md text-white text-sm px-3 py-1.5 whitespace-nowrap shadow-lg border border-white/10">
                                                {saving ? t('actions.saving') : t('actions.save')}
                                                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        )}

                        {/* Complete button */}
                        {onComplete && (
                            <motion.div className="relative" variants={staggerItem}>
                                <motion.button
                                    onClick={onComplete}
                                    onMouseEnter={() => setHoveredItem('complete')}
                                    onMouseLeave={() => setHoveredItem(null)}
                                    className="w-9 h-9 flex items-center justify-center transition-all duration-200 cursor-pointer bg-indigo-50 hover:bg-indigo-500 border-l-4 border-l-indigo-500 hover:border-l-indigo-600 border-y border-r border-indigo-200 text-indigo-700 hover:text-white"
                                    aria-label="Complete Certification"
                                    whileTap={{ scale: 0.95 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-2.094 3.419 3.419 0 004.438 0 3.42 3.42 0 001.946 2.094 3.419 3.419 0 003.352 3.352 3.42 3.42 0 002.094 1.946 3.419 3.419 0 000 4.438 3.42 3.42 0 00-2.094 1.946 3.419 3.419 0 00-3.352 3.352 3.42 3.42 0 00-1.946 2.094 3.419 3.419 0 00-4.438 0 3.42 3.42 0 00-1.946-2.094 3.419 3.419 0 00-3.352-3.352 3.42 3.42 0 00-2.094-1.946 3.419 3.419 0 000-4.438 3.42 3.42 0 002.094-1.946 3.419 3.419 0 003.352-3.352z" />
                                    </svg>
                                </motion.button>

                                <AnimatePresence>
                                    {hoveredItem === 'complete' && (
                                        <motion.div
                                            className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
                                            variants={tooltipVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <div className="bg-slate-800/80 backdrop-blur-md text-white text-sm px-3 py-1.5 whitespace-nowrap shadow-lg border border-white/10">
                                                {t('actions.complete')}
                                                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        )}

                        {/* Download PDF button */}
                        {onDownloadPdf && (
                            <motion.div className="relative" variants={staggerItem}>
                                <motion.button
                                    onClick={onDownloadPdf}
                                    disabled={downloadingPdf}
                                    onMouseEnter={() => setHoveredItem('download')}
                                    onMouseLeave={() => setHoveredItem(null)}
                                    className={`w-9 h-9 flex items-center justify-center transition-all duration-200 cursor-pointer ${downloadingPdf
                                        ? 'bg-slate-100 text-slate-400 border-l-2 border-l-slate-300 border-y border-r border-slate-200'
                                        : 'bg-slate-50 hover:bg-slate-600 border-l-4 border-l-slate-600 hover:border-l-slate-700 border-y border-r border-slate-200 text-slate-700 hover:text-white'
                                        }`}
                                    aria-label="Download PDF"
                                    whileTap={{ scale: 0.95 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                >
                                    {downloadingPdf ? (
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    )}
                                </motion.button>

                                <AnimatePresence>
                                    {hoveredItem === 'download' && (
                                        <motion.div
                                            className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
                                            variants={tooltipVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <div className="bg-slate-800/80 backdrop-blur-md text-white text-sm px-3 py-1.5 whitespace-nowrap shadow-lg border border-white/10">
                                                {downloadingPdf ? t('actions.generating') : t('actions.download')}
                                                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </motion.div>
                </>
            )}

            {/* Always show Download PDF if it's the only action, or if readOnly */}
            {(isReadOnly || (!onSave && !onComplete)) && onDownloadPdf && (
                <>
                    <motion.div className="w-8 mx-auto h-px bg-slate-200 my-6" variants={staggerItem} />
                    <motion.div className="flex flex-col gap-2" variants={staggerContainer}>
                        <motion.div className="relative" variants={staggerItem}>
                            <motion.button
                                onClick={onDownloadPdf}
                                disabled={downloadingPdf}
                                onMouseEnter={() => setHoveredItem('download')}
                                onMouseLeave={() => setHoveredItem(null)}
                                className={`w-9 h-9 flex items-center justify-center transition-all duration-200 cursor-pointer ${downloadingPdf
                                    ? 'bg-slate-100 text-slate-400 border-l-2 border-l-slate-300 border-y border-r border-slate-200'
                                    : 'bg-slate-50 hover:bg-slate-600 border-l-4 border-l-slate-600 hover:border-l-slate-700 border-y border-r border-slate-200 text-slate-700 hover:text-white'
                                    }`}
                                aria-label="Download PDF"
                                whileTap={{ scale: 0.95 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                            >
                                {downloadingPdf ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                )}
                            </motion.button>

                            <AnimatePresence>
                                {hoveredItem === 'download' && (
                                    <motion.div
                                        className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
                                        variants={tooltipVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                    >
                                        <div className="bg-slate-800/80 backdrop-blur-md text-white text-sm px-3 py-1.5 whitespace-nowrap shadow-lg border border-white/10">
                                            {downloadingPdf ? t('actions.generating') : t('actions.download')}
                                            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </motion.nav>
    );
}
